import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import NotFound from '@/pages/NotFound';
import { precisaConfirmarManual } from '@/lib/manualAck';

// Heavy authenticated pages are code-split; loaded on demand.
const UserDashboard = lazy(() => import('@/pages/UserDashboard'));
const Manual = lazy(() => import('@/pages/Manual'));
const OrgAdminLayout = lazy(() => import('@/layouts/OrgAdminLayout'));
const OrgAdminOrgRedirect = lazy(() => import('@/pages/org-admin/OrgRedirect'));
const SuperAdminLayout = lazy(() => import('@/layouts/SuperAdminLayout'));
const SuperAdminDashboardPage = lazy(() => import('@/pages/super-admin/DashboardPage'));
const SuperAdminOrganizationsPage = lazy(() => import('@/pages/super-admin/OrganizationsPage'));
const SuperAdminAuditPage = lazy(() => import('@/pages/super-admin/AuditPage'));
const SuperAdminAlertsPage = lazy(() => import('@/pages/super-admin/AlertsPage'));
const OrgAdminAlertsPage = lazy(() => import('@/pages/org-admin/AlertsPage'));
const OrgAdminIntegrarTotpPage = lazy(() => import('@/pages/org-admin/IntegrarTotpPage'));
const OrgAdminAuditPage = lazy(() => import('@/pages/org-admin/AuditPage'));
const OrgAdminTotpPage = lazy(() => import('@/pages/org-admin/TotpPage'));
const OrgAdminUsersPage = lazy(() => import('@/pages/org-admin/UsersPage'));
const OrgAdminGroupsPage = lazy(() => import('@/pages/org-admin/GroupsPage'));
const OrgAdminDashboardPage = lazy(() => import('@/pages/org-admin/DashboardPage'));
const OrgAdminServicesPage = lazy(() => import('@/pages/org-admin/ServicesPage'));
const OrgAdminCertificatesPage = lazy(() => import('@/pages/org-admin/CertificatesPage'));
const OrgAdminTribunaisPage = lazy(() => import('@/pages/org-admin/TribunaisPage'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Carregando...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Primeiro acesso (ou manual atualizado): exibe o manual antes de qualquer
  // outra tela. A própria rota /manual é isenta, senão o redirect entra em loop.
  if (location.pathname !== '/manual' && precisaConfirmarManual(user)) {
    return <Navigate to="/manual" replace />;
  }

  // Check role permissions
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'super_admin') return <Navigate to="/super-admin" />;
    if (user.role === 'org_admin') return <Navigate to="/org-admin" />;
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return children;
  }

  // Redirect based on user role
  if (user.role === 'super_admin') {
    return <Navigate to="/super-admin" />;
  }
  if (user.role === 'org_admin') {
    return <Navigate to="/org-admin" />;
  }
  return <Navigate to="/dashboard" />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route 
            path="/privacy" 
            element={
              <PrivacyPolicy />
            } 
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute allowedRoles={['user']}>
                <UserDashboard />
              </PrivateRoute>
            }
          />
          {/* Manual do usuário. Sem allowedRoles de propósito: qualquer usuário
              autenticado pode abrir (admins também usam a extensão). Restringir
              a ['user'] faria o PrivateRoute redirecionar admins para fora. */}
          <Route
            path="/manual"
            element={
              <PrivateRoute>
                <Manual />
              </PrivateRoute>
            }
          />
          <Route
            element={
              <PrivateRoute allowedRoles={['super_admin']}>
                <SuperAdminLayout />
              </PrivateRoute>
            }
          >
            <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
            <Route path="/super-admin/dashboard" element={<SuperAdminDashboardPage />} />
            <Route path="/super-admin/organizations" element={<SuperAdminOrganizationsPage />} />
            <Route path="/super-admin/audit" element={<SuperAdminAuditPage />} />
            <Route path="/super-admin/alerts" element={<SuperAdminAlertsPage />} />
            <Route path="/super-admin/*" element={<NotFound />} />
          </Route>
          <Route
            element={
              <PrivateRoute allowedRoles={['org_admin', 'super_admin']}>
                <OrgAdminLayout />
              </PrivateRoute>
            }
          >
            <Route path="/org-admin" element={<OrgAdminOrgRedirect />} />
            <Route path="/org-admin/:orgId" element={<OrgAdminOrgRedirect />} />
            <Route path="/org-admin/:orgId/alerts" element={<OrgAdminAlertsPage />} />
            <Route path="/org-admin/:orgId/integrar-totp" element={<OrgAdminIntegrarTotpPage />} />
            <Route path="/org-admin/:orgId/audit" element={<OrgAdminAuditPage />} />
            <Route path="/org-admin/:orgId/totp" element={<OrgAdminTotpPage />} />
            <Route path="/org-admin/:orgId/users" element={<OrgAdminUsersPage />} />
            <Route path="/org-admin/:orgId/groups" element={<OrgAdminGroupsPage />} />
            <Route path="/org-admin/:orgId/dashboard" element={<OrgAdminDashboardPage />} />
            <Route path="/org-admin/:orgId/services" element={<OrgAdminServicesPage />} />
            <Route path="/org-admin/:orgId/certificates" element={<OrgAdminCertificatesPage />} />
            <Route path="/org-admin/:orgId/tribunais" element={<OrgAdminTribunaisPage />} />
            <Route path="/org-admin/:orgId/*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}

export default App;
